﻿import type { GameConfig, GyroRate, MotionSample } from '../types';

export type JumpDetectorStatus = 'calibrating' | 'ready' | 'jump' | 'gyro_assisted_jump' | 'rejected_spin' | 'ignored';

export type JumpDetectorRejectionReason = 'missing_axes' | 'spin_too_high' | 'cooldown' | 'not_recovered';

export type JumpDetectorEvent = {
  status: JumpDetectorStatus;
  jumpCount: number;
  magnitude: number;
  signal: number;
  threshold: number;
  gyroMagnitude: number | null;
  confidence: number;
  rejectionReason?: JumpDetectorRejectionReason;
};

export type JumpDetectorOptions = Pick<GameConfig, 'minValidIntervalMs' | 'maxValidIntervalMs'> & {
  calibrationMs?: number;
  minCalibrationSamples?: number;
  lowPassAlpha?: number;
  minPeakSignal?: number;
  noiseMultiplier?: number;
  recoveryRatio?: number;
  gyroSoftSpinDps?: number;
  gyroHardSpinDps?: number;
  gyroAssistMaxDps?: number;
  accSoftPeakRatio?: number;
  enableGyroAssist?: boolean;
  requireNegativeRecovery?: boolean;
  negativeRecoveryRatio?: number;
};

export class JumpDetector {
  private readonly minValidIntervalMs: number;
  private readonly maxValidIntervalMs: number;
  private readonly calibrationMs: number;
  private readonly minCalibrationSamples: number;
  private readonly lowPassAlpha: number;
  private readonly minPeakSignal: number;
  private readonly noiseMultiplier: number;
  private readonly recoveryRatio: number;
  private readonly gyroSoftSpinDps: number;
  private readonly gyroHardSpinDps: number;
  private readonly gyroAssistMaxDps: number;
  private readonly accSoftPeakRatio: number;
  private readonly enableGyroAssist: boolean;
  private readonly requireNegativeRecovery: boolean;
  private readonly negativeRecoveryRatio: number;

  private lowPass: number | null = null;
  private calibrationStartedAt: number | null = null;
  private calibrationSignals: number[] = [];
  private calibrated = false;
  private armed = true;
  private lastJumpAt = Number.NEGATIVE_INFINITY;
  private jumps = 0;
  private peakThreshold = 2.2;

  constructor(options: JumpDetectorOptions) {
    this.minValidIntervalMs = options.minValidIntervalMs;
    this.maxValidIntervalMs = options.maxValidIntervalMs;
    this.calibrationMs = options.calibrationMs ?? 1200;
    this.minCalibrationSamples = options.minCalibrationSamples ?? 12;
    this.lowPassAlpha = options.lowPassAlpha ?? 0.9;
    this.minPeakSignal = options.minPeakSignal ?? 2.0;
    this.noiseMultiplier = options.noiseMultiplier ?? 3.2;
    this.recoveryRatio = options.recoveryRatio ?? 0.72;
    this.gyroSoftSpinDps = options.gyroSoftSpinDps ?? 160;
    this.gyroHardSpinDps = options.gyroHardSpinDps ?? 420;
    this.gyroAssistMaxDps = options.gyroAssistMaxDps ?? 120;
    this.accSoftPeakRatio = options.accSoftPeakRatio ?? 0.82;
    this.enableGyroAssist = options.enableGyroAssist ?? true;
    this.requireNegativeRecovery = options.requireNegativeRecovery ?? false;
    this.negativeRecoveryRatio = options.negativeRecoveryRatio ?? 0.45;
  }

  get jumpCount(): number {
    return this.jumps;
  }

  get isCalibrated(): boolean {
    return this.calibrated;
  }

  get threshold(): number {
    return this.peakThreshold;
  }

  reset(): void {
    this.lowPass = null;
    this.calibrationStartedAt = null;
    this.calibrationSignals = [];
    this.calibrated = false;
    this.armed = true;
    this.lastJumpAt = Number.NEGATIVE_INFINITY;
    this.jumps = 0;
    this.peakThreshold = 2.2;
  }

  process(sample: MotionSample): JumpDetectorEvent {
    const magnitude = vectorMagnitude(sample);
    const gyroMagnitude = rotationMagnitude(sample.rotationRate);
    if (magnitude === null) {
      return this.event('ignored', 0, 0, gyroMagnitude, 0, 'missing_axes');
    }

    const signedSignal = this.highPassSignal(magnitude);
    const signal = Math.max(0, signedSignal);

    if (!this.calibrated) {
      this.collectCalibration(sample.timestamp, Math.abs(signedSignal));
      return this.event(this.calibrated ? 'ready' : 'calibrating', magnitude, signal, gyroMagnitude, this.confidenceForGyro(gyroMagnitude));
    }

    const recoveryThreshold = this.peakThreshold * this.recoveryRatio;
    const elapsedSinceJump = sample.timestamp - this.lastJumpAt;
    if (elapsedSinceJump > this.maxValidIntervalMs) {
      this.armed = true;
    }

    const recoveredForNextRep = this.requireNegativeRecovery
      ? signedSignal <= -this.peakThreshold * this.negativeRecoveryRatio
      : signedSignal <= recoveryThreshold;
    if (recoveredForNextRep) {
      this.armed = true;
    }

    const canCountByCooldown = elapsedSinceJump >= this.minValidIntervalMs;
    const canCountByRecovery = this.armed;
    const signalCrossedPeak = signal >= this.peakThreshold;
    const signalCrossedSoftPeak = signal >= this.peakThreshold * this.accSoftPeakRatio;
    const spinTooHigh = gyroMagnitude !== null && gyroMagnitude > this.gyroHardSpinDps;

    if (spinTooHigh && canCountByCooldown && signalCrossedSoftPeak) {
      return this.event('rejected_spin', magnitude, signal, gyroMagnitude, 0.15, 'spin_too_high');
    }

    if (canCountByRecovery && canCountByCooldown && signalCrossedPeak) {
      this.jumps += 1;
      this.lastJumpAt = sample.timestamp;
      this.armed = false;
      return this.event('jump', magnitude, signal, gyroMagnitude, this.confidenceForGyro(gyroMagnitude));
    }

    const gyroStableEnoughToAssist = gyroMagnitude !== null && gyroMagnitude <= this.gyroAssistMaxDps;
    if (this.enableGyroAssist && canCountByRecovery && canCountByCooldown && signalCrossedSoftPeak && gyroStableEnoughToAssist) {
      this.jumps += 1;
      this.lastJumpAt = sample.timestamp;
      this.armed = false;
      return this.event('gyro_assisted_jump', magnitude, signal, gyroMagnitude, 0.72);
    }

    return this.event('ready', magnitude, signal, gyroMagnitude, this.confidenceForGyro(gyroMagnitude), canCountByCooldown ? 'not_recovered' : 'cooldown');
  }

  forceReady(noiseSignals: number[] = [0.08, 0.1, 0.12, 0.09]): void {
    this.calibrationSignals = noiseSignals;
    this.finishCalibration();
  }

  private highPassSignal(magnitude: number): number {
    if (this.lowPass === null) {
      this.lowPass = magnitude;
      return 0;
    }
    this.lowPass = this.lowPassAlpha * this.lowPass + (1 - this.lowPassAlpha) * magnitude;
    return magnitude - this.lowPass;
  }

  private collectCalibration(timestamp: number, signal: number): void {
    if (this.calibrationStartedAt === null) {
      this.calibrationStartedAt = timestamp;
    }
    this.calibrationSignals.push(signal);
    const enoughTime = timestamp - this.calibrationStartedAt >= this.calibrationMs;
    const enoughSamples = this.calibrationSignals.length >= this.minCalibrationSamples;
    if ((enoughTime && enoughSamples) || (this.calibrationMs === 0 && enoughSamples)) {
      this.finishCalibration();
    }
  }

  private finishCalibration(): void {
    const signals = this.calibrationSignals.length > 0 ? this.calibrationSignals : [0];
    const meanSignal = mean(signals);
    const noise = standardDeviation(signals, meanSignal);
    this.peakThreshold = Math.max(this.minPeakSignal, meanSignal + noise * this.noiseMultiplier);
    this.calibrated = true;
    this.armed = true;
  }

  private event(
    status: JumpDetectorStatus,
    magnitude: number,
    signal: number,
    gyroMagnitude: number | null,
    confidence: number,
    rejectionReason?: JumpDetectorRejectionReason
  ): JumpDetectorEvent {
    return {
      status,
      jumpCount: this.jumps,
      magnitude,
      signal,
      threshold: this.peakThreshold,
      gyroMagnitude,
      confidence,
      ...(rejectionReason ? { rejectionReason } : {})
    };
  }

  private confidenceForGyro(gyroMagnitude: number | null): number {
    if (gyroMagnitude === null) return 0.82;
    if (gyroMagnitude <= this.gyroAssistMaxDps) return 0.95;
    if (gyroMagnitude >= this.gyroHardSpinDps) return 0.2;
    const unstableRange = this.gyroHardSpinDps - this.gyroSoftSpinDps;
    if (unstableRange <= 0) return 0.75;
    const instability = Math.max(0, Math.min(1, (gyroMagnitude - this.gyroSoftSpinDps) / unstableRange));
    return Math.max(0.35, 0.9 - instability * 0.55);
  }
}

export function vectorMagnitude(sample: MotionSample): number | null {
  const { x, y, z } = sample;
  if (!isFiniteAxis(x) || !isFiniteAxis(y) || !isFiniteAxis(z)) return null;
  return Math.sqrt(x * x + y * y + z * z);
}

export function rotationMagnitude(rotationRate: GyroRate | null | undefined): number | null {
  if (!rotationRate) return null;
  const { alpha, beta, gamma } = rotationRate;
  if (!isFiniteAxis(alpha) || !isFiniteAxis(beta) || !isFiniteAxis(gamma)) return null;
  return Math.sqrt(alpha * alpha + beta * beta + gamma * gamma);
}

function isFiniteAxis(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function mean(values: number[]): number {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function standardDeviation(values: number[], avg: number): number {
  const variance = values.reduce((sum, value) => sum + (value - avg) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}
